import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Zap, Wind, Droplets, Battery, Grid3X3, HelpCircle,
  Search, Eye, Pencil, Trash2, Loader2, Download,
  CheckCircle, AlertTriangle, XCircle, Clock
} from 'lucide-react';
import { assetsAPI, exportAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useExport } from '../../hooks/useExport';
import toast from 'react-hot-toast';
import AssetModal from './AssetModal';
import clsx from 'clsx';

const TYPE_ICONS = {
  solar:   { icon: Zap,        color: 'text-amber-500',  bg: 'bg-amber-50'  },
  wind:    { icon: Wind,       color: 'text-blue-500',   bg: 'bg-blue-50'   },
  hydro:   { icon: Droplets,   color: 'text-cyan-500',   bg: 'bg-cyan-50'   },
  battery: { icon: Battery,    color: 'text-purple-500', bg: 'bg-purple-50' },
  grid:    { icon: Grid3X3,    color: 'text-gray-500',   bg: 'bg-gray-50'   },
  other:   { icon: HelpCircle, color: 'text-gray-400',   bg: 'bg-gray-50'   },
};

const STATUS_BADGE = {
  operational:    { label: 'Operational',    icon: CheckCircle,  cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  maintenance:    { label: 'Maintenance',    icon: Clock,        cls: 'bg-amber-50 text-amber-700 ring-amber-200'       },
  offline:        { label: 'Offline',        icon: XCircle,      cls: 'bg-red-50 text-red-700 ring-red-200'             },
  decommissioned: { label: 'Decommissioned', icon: AlertTriangle,cls: 'bg-gray-50 text-gray-500 ring-gray-200'          },
};

const ASSET_TYPES = ['solar','wind','hydro','battery','grid','other'];
const STATUSES    = ['operational','maintenance','offline','decommissioned'];

export function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.offline;
  const Icon = s.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1', s.cls)}>
      <Icon size={11} />{s.label}
    </span>
  );
}

export function TypeIcon({ type, size = 'md' }) {
  const t = TYPE_ICONS[type] || TYPE_ICONS.other;
  const Icon = t.icon;
  const sz = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';
  const ic = size === 'sm' ? 14 : 18;
  return (
    <div className={clsx('rounded-lg flex items-center justify-center shrink-0', sz, t.bg)}>
      <Icon size={ic} className={t.color} />
    </div>
  );
}

export default function AssetsPage() {
  const navigate    = useNavigate();
  const { hasRole } = useAuth();
  const canWrite    = hasRole('super_admin','operations_mgr');
  const { download, loading: exporting } = useExport();

  const [assets, setAssets]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [typeFilter, setType]     = useState('');
  const [statusFilter, setStatus] = useState('');
  const [page, setPage]           = useState(1);
  const [viewMode, setViewMode]   = useState('table');
  const [modalOpen, setModalOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await assetsAPI.list({ page, limit, search: search||undefined, type: typeFilter||undefined, status: statusFilter||undefined });
      setAssets(data.data.items);
      setTotal(data.data.pagination.total);
    } catch {}
    finally { setLoading(false); }
  }, [page, search, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, typeFilter, statusFilter]);

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete "${asset.name}"?`)) return;
    try { await assetsAPI.remove(asset.id); toast.success('Asset deleted'); load(); } catch {}
  };

  const totalPages = Math.ceil(total / limit);
  const counts = assets.reduce((acc,a) => { acc[a.status]=(acc[a.status]||0)+1; return acc; }, {});

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Asset Register</h1>
          <p className="text-sm text-gray-500">{total} assets in portfolio</p>
        </div>
        {canWrite && (
          <button onClick={() => { setEditAsset(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium">
            <Plus size={16} /> Add Asset
          </button>
        )}
        <div className="flex gap-2">
          <button onClick={() => download(exportAPI.assetsPDF, 'assets.pdf')} disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <Download size={14}/> PDF
          </button>
          <button onClick={() => download(exportAPI.assetsExcel, 'assets.xlsx')} disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <Download size={14}/> Excel
          </button>
        </div>
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(statusFilter===s?'':s)}
            className={clsx('bg-white rounded-xl border p-4 text-left transition-all hover:shadow-md',
              statusFilter===s ? 'border-primary-500 ring-2 ring-primary-500/20' : 'border-gray-200')}>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{STATUS_BADGE[s].label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{counts[s]||0}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, code, location…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
        </div>
        <select value={typeFilter} onChange={e=>setType(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">
          <option value="">All Types</option>
          {ASSET_TYPES.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
        </select>
        <select value={statusFilter} onChange={e=>setStatus(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">
          <option value="">All Statuses</option>
          {STATUSES.map(s=><option key={s} value={s}>{STATUS_BADGE[s].label}</option>)}
        </select>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden ml-auto">
          {['table','grid'].map(m=>(
            <button key={m} onClick={()=>setViewMode(m)}
              className={clsx('px-3 py-2 text-xs font-medium transition-colors capitalize',
                viewMode===m?'bg-primary-500 text-white':'text-gray-500 hover:bg-gray-50')}>{m}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      {viewMode==='table' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  {['Asset','Type','Location','Capacity','Status','Install Date',''].map(h=>(
                    <th key={h} className={clsx('px-4 py-3 font-medium', h===''?'text-right':'text-left')}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-12"><Loader2 size={20} className="animate-spin text-gray-400 mx-auto" /></td></tr>
                ) : assets.length===0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                    No assets found. {canWrite && <button onClick={()=>{setEditAsset(null);setModalOpen(true);}} className="text-primary-500 underline">Add your first asset.</button>}
                  </td></tr>
                ) : assets.map((a,i) => (
                  <tr key={a.id} className={clsx('hover:bg-hover transition-colors cursor-pointer', i%2===0?'bg-white':'bg-surface')}
                    onClick={()=>navigate(`/assets/${a.id}`)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <TypeIcon type={a.asset_type} size="sm" />
                        <div><p className="font-medium text-gray-900">{a.name}</p><p className="text-xs text-gray-400">{a.asset_code}</p></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-600">{a.asset_type}</td>
                    <td className="px-4 py-3 text-gray-600">{a.location_name||'—'}</td>
                    <td className="px-4 py-3 text-gray-600 font-medium">{a.capacity_mw?`${a.capacity_mw} MW`:'—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3 text-gray-600">{a.install_date?new Date(a.install_date).toLocaleDateString('en-GB'):'—'}</td>
                    <td className="px-4 py-3" onClick={e=>e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={()=>navigate(`/assets/${a.id}`)} className="p-1.5 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"><Eye size={15}/></button>
                        {canWrite && <>
                          <button onClick={()=>{setEditAsset(a);setModalOpen(true);}} className="p-1.5 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"><Pencil size={15}/></button>
                          <button onClick={()=>handleDelete(a)} className="p-1.5 text-gray-400 hover:text-danger hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={15}/></button>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages>1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
              <span>{total} total · Page {page} of {totalPages}</span>
              <div className="flex gap-1">
                <button disabled={page===1} onClick={()=>setPage(p=>p-1)} className="px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-xs">Previous</button>
                <button disabled={page===totalPages} onClick={()=>setPage(p=>p+1)} className="px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-xs">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Grid */}
      {viewMode==='grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? <div className="col-span-full flex justify-center py-12"><Loader2 size={20} className="animate-spin text-gray-400"/></div>
          : assets.length===0 ? <div className="col-span-full text-center py-12 text-gray-400">No assets found.</div>
          : assets.map(a=>(
            <div key={a.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <TypeIcon type={a.asset_type} />
                <StatusBadge status={a.status} />
              </div>
              <h3 className="font-semibold text-gray-900 truncate">{a.name}</h3>
              <p className="text-xs text-gray-400 mb-3">{a.asset_code}</p>
              <div className="space-y-1 text-xs text-gray-500 mb-4">
                <p>{a.location_name||'No location set'}</p>
                <p className="font-medium text-gray-700">{a.capacity_mw?`${a.capacity_mw} MW`:'Capacity not set'}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={()=>navigate(`/assets/${a.id}`)} className="flex-1 py-1.5 text-xs text-center bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 font-medium">View Details</button>
                {canWrite && <button onClick={()=>{setEditAsset(a);setModalOpen(true);}} className="px-3 py-1.5 text-xs bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100"><Pencil size={12}/></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && <AssetModal asset={editAsset} onClose={()=>setModalOpen(false)} onSaved={()=>{setModalOpen(false);load();}} />}
    </div>
  );
}
